import { useSocket } from "common/hooks/useSocket";
import React, { useContext } from "react";
import { DraftContext } from "../context";
import { Divider, IconButton, Stack } from "@mui/material";
import { DraftCharacter, DraftStep } from "types/Draft/type";
import { Close, DoNotDisturb } from "@mui/icons-material";
import Constellation from "common/components/Constellation";
import {
  BanPaperStyled,
  BannedCharacter,
  CardMediaBanStyled,
  PulsingBanPaper,
} from "common/components/DraftCharacterCard";

type BansListProps = {
  player: "Player 1" | "Player 2";
  color: string;
  banSteps: DraftStep[];
  bans: DraftCharacter[];
  hiddenBans?: DraftCharacter[];
  selected?: DraftCharacter[];
  hideBans?: boolean;
  onClick?: (character: DraftCharacter) => void;
};

const BansList = (props: BansListProps) => {
  const {
    player,
    color,
    banSteps,
    bans,
    hiddenBans,
    selected,
    hideBans = false,
    onClick,
  } = props;
  const { currentStep } = useContext(DraftContext);
  const isCurrentBan =
    currentStep?.action === "Ban" &&
    (currentStep.player === "Both" || currentStep.player === player);

  const getCharacterCard = (iStep: number, iQuantity: number) => {
    const index =
      banSteps
        .slice(0, iStep)
        .reduce((acc, { quantity }) => acc + quantity, 0) + iQuantity;

    if (bans[index] !== undefined) {
      return (
        <BannedCharacter
          key={`${color}-${iStep}-${iQuantity}`}
          character={bans[index]}
        />
      );
    } else if (hiddenBans !== undefined && hiddenBans[index] !== undefined) {
      const character = hiddenBans[index];
      return (
        <BanPaperStyled key={`${color}-${iStep}-${iQuantity}`}>
          <CardMediaBanStyled
            characterName={hideBans ? "Voyageur" : character.name}
            sx={{
              filter: hideBans ? "grayscale(1)" : "none",
            }}
          />
        </BanPaperStyled>
      );
    } else if (
      isCurrentBan &&
      selected !== undefined &&
      index >= bans.length &&
      index - bans.length < (currentStep?.quantity ?? 0)
    ) {
      const character = selected[index - bans.length];
      return (
        <PulsingBanPaper key={`${color}-${iStep}-${iQuantity}`}>
          {character !== undefined ? (
            <>
              <Stack
                sx={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "12px",
                  position: "absolute",
                  overflow: "hidden",
                }}
              >
                {!hideBans && character.p1Constellation !== undefined && (
                  <Constellation player="player1">
                    {character.p1Constellation}
                  </Constellation>
                )}
                {!hideBans && character.p2Constellation !== undefined && (
                  <Constellation player="player2">
                    {character.p2Constellation}
                  </Constellation>
                )}
              </Stack>
              <CardMediaBanStyled
                characterName={hideBans ? "Voyageur" : character.name}
                sx={{
                  filter: hideBans ? "grayscale(1)" : "none",
                }}
              />
              {onClick && (
                <Stack
                  alignItems="center"
                  sx={{
                    position: "absolute",
                    bottom: "-14px",
                    width: "100%",
                  }}
                >
                  <IconButton
                    onClick={() => onClick(character)}
                    sx={{
                      padding: "2px",
                      margin: "auto",
                      bgcolor: ({ palette }) => palette.common.player2.banCard,
                      "&:hover": {
                        bgcolor: ({ palette }) => palette.common.player2.paper,
                      },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Stack>
              )}
            </>
          ) : (
            ""
          )}
        </PulsingBanPaper>
      );
    } else {
      return (
        <BanPaperStyled
          key={`${color}-${iStep}-${iQuantity}`}
          sx={{ padding: "0.5rem" }}
        >
          <DoNotDisturb sx={{ width: "100%", height: "100%" }} />
        </BanPaperStyled>
      );
    }
  };

  return (
    <Stack direction="row" gap={1} padding={1} height="fit-content">
      {banSteps.map((step, iStep) => (
        <Stack key={`${color}-${iStep}`} direction="row" gap={1}>
          {[...Array(step.quantity).keys()].map((iQuantity) =>
            getCharacterCard(iStep, iQuantity),
          )}
          {banSteps.length > 1 && iStep !== banSteps.length - 1 ? (
            <Divider key={`divider-${iStep}`} orientation="vertical" />
          ) : (
            ""
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export default BansList;
